<template>
  <!-- full screen on a phone (below Vuetify's 600px `sm`): the form is
       nearly three screens tall and every field needs the width -->
  <v-dialog
    v-model="dialog"
    max-width="960px"
    :fullscreen="$vuetify.display.xs"
  >
    <v-dialog v-model="landingPageSettings" max-width="500">
      <v-card class="unmask-form ln-dialog-card">
        <v-card-title>
          <v-row justify="space-between" align="center">
            <v-col cols="12" sm> Landing page settings </v-col>

            <v-col>
              <v-tooltip
                v-if="
                  modelView === 'editLink' &&
                  itHasLandingPage === true &&
                  desktopUserOption === 'landing_page'
                "
                location="bottom"
              >
                <template v-slot:activator="{ props }">
                  <v-btn
                    :loading="regenerateLandingPageLoading"
                    @click="regenerateLandingPage"
                    v-bind="props"
                    variant="outlined"
                    style="float: right"
                    size="small"
                    class="landing-regenerate"
                  >
                    <v-icon start>mdi-refresh</v-icon>
                    Regenerate
                  </v-btn>
                </template>
                <p style="max-width: 400px">
                  If you have changed the app icon or description on the Play
                  Store you can trigger the regeneration of the landing page, in
                  this way the changes will be reflected on the landing page
                </p>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text class="mt-5">
          <v-alert
            class="my-7"
            color="blue"
            density="compact"
            type="info"
            v-if="userPlan.plan.name == 'free'"
            >Upgrade your plan to customize the landing page</v-alert
          >

          <div
            :style="{
              opacity: userPlan.plan.name == 'free' ? 0.4 : undefined,
              pointerEvents: userPlan.plan.name == 'free' ? 'none' : undefined,
            }"
          >
            <v-text-field
              label="App title"
              variant="outlined"
              validate-on="blur"
              :rules="landingPageTitleRules"
              v-model="landingPageTitle"
            ></v-text-field>

            <v-text-field
              label="App description"
              variant="outlined"
              validate-on="blur"
              :rules="landingPageDescriptionRules"
              v-model="landingPageDescription"
            ></v-text-field>

            <v-text-field
              label="App image url"
              variant="outlined"
              hint="Must be the app icon uploaded to Google Play store or App Store"
              validate-on="blur"
              :rules="landingPageAppIconURLRules"
              v-model="landingPageAppIconURL"
            ></v-text-field>

            <div class="mt-3" style="display: flex">
              <p class="mt-1"><label>App color</label></p>
              <input
                type="color"
                name="landing page color"
                class="ml-4"
                v-model="landingPageColor"
              />
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <!-- v-card-actions hands its buttons `variant="text"`, and a
               disabled text/plain button in Vuetify 4 keeps its colour and
               drops to 26% opacity instead of going grey the way Vuetify 2
               did: the Save button all but vanished. `flat` gives the
               disabled state a grey fill again. -->
          <v-btn
            color="red"
            variant="text"
            @click="landingPageSettings = false"
          >
            Cancel
          </v-btn>

          <v-btn
            :disabled="
              userPlan.plan.name == 'free' || isLandingPageValid !== true
            "
            color="green-darken-1"
            variant="flat"
            @click="saveLandingPageSettings"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card
      v-if="modelView === 'shareLink'"
      class="ln-dialog-card"
      data-testid="link-dialog-share"
    >
      <v-btn
        v-if="$vuetify.display.xs"
        icon
        variant="text"
        aria-label="Close"
        class="link-dialog-close"
        @click="close"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-container>
        <v-card-text>
          <p class="text-headline-small text-center">
            {{ shareDialogTitle || "Your Link is now live" }}
          </p>
          <v-row>
            <v-col align="center" justify="center">
              <v-text-field
                rounded
                v-model="fullUrl"
                color="primary"
                readonly
                class="share-url"
                ref="fullUrlInput"
                data-testid="link-dialog-share-url"
              >
                <template v-slot:append-inner>
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon @click="copyFullUrl" v-bind="props">{{
                        fullUrlCopied ? "mdi-check" : "mdi-content-copy"
                      }}</v-icon>
                    </template>
                    <span>Copy to clipboard</span>
                  </v-tooltip>
                </template>
                <template v-slot:append>
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        icon
                        v-bind="props"
                        :href="fullUrl"
                        target="_blank"
                      >
                        <v-icon>mdi-open-in-new</v-icon>
                      </v-btn>
                    </template>
                    <span>Open link in a new tab</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row align="center" justify="center">
            <v-col cols="12" sm="4" md="3" class="text-center">
              <img
                :src="qrCodeImgSrc"
                alt="qr code loading"
                class="qr-code-image"
              />
            </v-col>
            <v-col cols="12" sm="8" md="4">
              <QrDownloadMenu :full-url="fullUrl">
                <template #default="{ menuProps, download }">
                  <div class="qr-split mb-1">
                    <v-btn
                      class="qr-split__main"
                      variant="tonal"
                      @click="download()"
                      >Download QR Code image</v-btn
                    >
                    <v-btn
                      class="qr-split__more"
                      variant="tonal"
                      aria-label="Other QR code formats"
                      v-bind="menuProps"
                      ><v-icon>mdi-menu-down</v-icon></v-btn
                    >
                  </div>
                </template>
              </QrDownloadMenu>
              <br />
              <v-btn class="mb-1" variant="tonal" block :href="emailLink"
                >Send link via E-mail</v-btn
              >
              <br />
              <v-btn
                class="mb-1"
                variant="tonal"
                block
                :href="twitterLink"
                target="_blank"
                >Share link on Twitter</v-btn
              >
            </v-col>
          </v-row>
        </v-card-text>
      </v-container>
    </v-card>
    <v-card v-else class="unmask-form ln-dialog-card" data-testid="link-dialog">
      <v-overlay
        :model-value="isLoading"
        contained
        persistent
        class="align-center justify-center"
      >
        <v-progress-circular indeterminate size="64" />
      </v-overlay>
      <!-- a full screen dialog has no backdrop left to tap -->
      <v-btn
        v-if="$vuetify.display.xs"
        icon
        variant="text"
        aria-label="Close"
        class="link-dialog-close"
        @click="close"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-card-title>
        <v-row align="center" justify="space-between" style="width: 100%">
          <v-col cols="auto" class="pa-0 link-dialog-title">
            <span class="text-headline-small d-flex align-center">
              <v-icon class="mr-2">mdi-link</v-icon>
              <span v-if="modelView == 'createLink'"
                >New Link
                {{
                  linkType === "simple" ? "(Simple)" : "(Deep linking)"
                }}</span
              >
              <span v-else>Edit {{ shortHandle }} Link</span>
            </span>
          </v-col>
          <v-col cols="12" sm="auto" class="pa-0">
            <div
              v-if="modelView == 'createLink'"
              class="d-flex flex-column align-start align-sm-end link-type-picker"
            >
              <span
                class="text-body-small mb-1"
                style="letter-spacing: 0.5px; color: #666"
                >Link Type</span
              >
              <div
                class="d-flex flex-wrap flex-sm-nowrap align-center"
                style="gap: 12px"
              >
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-chip
                      v-bind="props"
                      @click="linkType = 'simple'"
                      :class="['option-chip', 'mr-2']"
                      :style="{
                        cursor: 'pointer',
                        minWidth: '120px',
                        fontWeight: 500,
                        border:
                          linkType === 'simple' ? '2px solid #1976d2' : '',
                        boxShadow:
                          linkType === 'simple'
                            ? '0 0 0 2px rgba(25, 118, 210, 0.15)'
                            : '',
                      }"
                      label
                    >
                      <v-icon start size="small">mdi-link-variant</v-icon>
                      Simple
                      <v-icon end size="small" v-if="linkType === 'simple'"
                        >mdi-check-circle</v-icon
                      >
                    </v-chip>
                  </template>
                  <span
                    >Allow users to install the app. Mobile users will be
                    redirected to the app store,<br />
                    or website/landing page for other devices.</span
                  >
                </v-tooltip>
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-chip
                      v-bind="props"
                      @click="linkType = 'deeplinking'"
                      :class="['option-chip']"
                      :style="{
                        cursor: 'pointer',
                        minWidth: '160px',
                        fontWeight: 500,
                        border:
                          linkType === 'deeplinking' ? '2px solid #1976d2' : '',
                        boxShadow:
                          linkType === 'deeplinking'
                            ? '0 0 0 2px rgba(25, 118, 210, 0.15)'
                            : '',
                      }"
                      label
                    >
                      <v-icon start size="small">mdi-arrow-decision</v-icon>
                      Deep Link Support
                      <v-icon end size="small" v-if="linkType === 'deeplinking'"
                        >mdi-check-circle</v-icon
                      >
                    </v-chip>
                  </template>
                  <span
                    >Opens the app if installed to a specific view, otherwise
                    redirects to the app store for installation,<br />
                    or website/landing page for other devices.</span
                  >
                </v-tooltip>
              </div>
            </div>
            <div
              v-else-if="modelView == 'editLink'"
              class="d-flex flex-column align-start align-sm-end"
              style="min-width: 200px"
            >
              <span
                class="text-body-small mb-1"
                style="letter-spacing: 0.5px; color: #666"
                >Link Type</span
              >
              <v-chip
                :class="[
                  'option-chip',
                  linkType === 'deeplinking' ? '' : 'mr-2',
                ]"
                :style="{
                  minWidth: linkType === 'deeplinking' ? '160px' : '120px',
                  fontWeight: 500,
                  border:
                    linkType === 'simple'
                      ? '2px solid #1976d2'
                      : linkType === 'deeplinking'
                        ? '2px solid #1976d2'
                        : '',
                  boxShadow:
                    linkType === 'simple' || linkType === 'deeplinking'
                      ? '0 0 0 2px rgba(25, 118, 210, 0.15)'
                      : '',
                  background: '#fafbfc',
                  color: '#1976d2',
                  cursor: 'default',
                }"
                label
                disabled
              >
                <v-icon start size="small" v-if="linkType === 'simple'"
                  >mdi-link-variant</v-icon
                >
                <v-icon start size="small" v-else>mdi-arrow-decision</v-icon>
                <template v-if="linkType === 'simple'">Simple</template>
                <template v-else>Deep Linking</template>
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <p class="text-medium-emphasis text-body-large mt-5">
          <span v-if="linkType === 'simple'">
            {{
              modelView == "createLink" ? "Create a new link" : "Edit your link"
            }}
            by providing Google Play Store and Apple App Store URLs (optionally
            you can also provide Huawei AppGallery URL for the devices that
            don't support Google Play Store), users will be automatically
            redirected.
          </span>
          <span v-else>
            {{
              modelView == "createLink" ? "Create a new link" : "Edit your link"
            }}
            by selecting the deep linking app users will be automatically
            redirected to the specified view if the app is installed, otherwise
            they will be redirected to the app store for installation or
            website/landing page for other devices.
          </span>
        </p>
        <v-container>
          <!-- the fields validate on `invalid-input`: checked when you leave
               them, then on every keystroke while wrong, so an error goes as
               soon as it is fixed (with plain `blur` it stayed until you left
               the field again) -->
          <!-- Deep Linking Fields (Improved UI) -->
          <v-row v-if="linkType === 'deeplinking'">
            <v-col cols="12" md="12">
              <v-card variant="outlined" class="pa-4">
                <v-card-title class="pb-1 d-flex align-center">
                  <span class="text-title-medium font-weight-bold mr-2"
                    >Deep Linking Details</span
                  >
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon color="primary" size="small" v-bind="props"
                        >mdi-information-outline</v-icon
                      >
                    </template>
                    <span
                      >Configure the app and path for deep linking. This lets
                      users land directly on a specific view or resource in your
                      app.</span
                    >
                  </v-tooltip>
                </v-card-title>
                <v-card-text class="pt-0">
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="mb-3 font-weight-medium">
                        Deep Linking App
                      </div>
                      <v-select
                        v-model="deepLinkingAppSelected"
                        :items="deepLinkingDomains"
                        item-title="appName"
                        item-value="domain"
                        label="Select App"
                        required
                        density="compact"
                        :rules="[(v) => !!v || 'Select an app']"
                        prepend-icon="mdi-apps"
                        class="deep-linking-select"
                      >
                        <!-- Vuetify 4's item/selection slots hand over the
                             ORIGINAL item as `item` (Vuetify 3 handed over the
                             internal wrapper, whose `.raw` held it). Reading
                             `item.raw.appName` here threw, which silently blanked
                             the whole Deep Linking Details block. `internalItem`
                             is the wrapper, used for its `title` fallback when
                             the model holds a domain the list hasn't loaded. -->
                        <template v-slot:item="{ item, props }">
                          <v-list-item v-bind="props" :title="item.appName">
                            <template v-slot:prepend v-if="item.iconUrl">
                              <img
                                :src="item.iconUrl"
                                :alt="item.appName"
                                class="deep-linking-select__icon mr-3"
                              />
                            </template>
                          </v-list-item>
                        </template>
                        <template v-slot:selection="{ item, internalItem }">
                          <div
                            class="deep-linking-select__selection d-flex align-center"
                          >
                            <img
                              v-if="item.iconUrl"
                              :src="item.iconUrl"
                              :alt="item.appName"
                              class="deep-linking-select__icon mr-3"
                            />
                            <span>{{
                              item.appName || internalItem.title
                            }}</span>
                          </div>
                        </template>
                        <template v-slot:append-item>
                          <v-divider class="mb-1"></v-divider>
                          <v-list-item href="/#/deep-linking">
                            <template v-slot:prepend>
                              <v-icon>mdi-plus</v-icon>
                            </template>

                            <v-list-item-title class="text-grey-darken-2"
                              >Add new app</v-list-item-title
                            >
                          </v-list-item>
                        </template>
                      </v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="mb-3 font-weight-medium">
                        Deep Linking Path/View
                      </div>
                      <v-text-field
                        v-model="deepLinkingPath"
                        label="Enter Path"
                        placeholder="/d/product/123"
                        required
                        density="compact"
                        prepend-icon="mdi-link-variant"
                        class="deep-linking-path"
                        validate-on="invalid-input"
                        :rules="[
                          (v) => !!v || 'Path is required',
                          (v) =>
                            (typeof v === 'string' && v.startsWith('/d/')) ||
                            'Path must start with /d/',
                        ]"
                        :error="!isDeepLinkingPathValid"
                        :hint="
                          isDeepLinkingPathValid
                            ? 'Example: /d/product/123'
                            : 'Path must start with /d/'
                        "
                        persistent-hint
                        @blur="autoPrefixDeepLinkingPath"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-alert
                    type="info"
                    density="compact"
                    class="mt-2 mb-0"
                    border="start"
                    color="primary"
                    variant="tonal"
                  >
                    If the app is installed, users will be directed to this
                    specific path in your app.
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <div class="short-handle-section">
            <p class="text-center text-body-large mb-4 font-weight-medium">
              Domain and Short Handle
            </p>

            <v-row justify="center">
              <v-col cols="12" md="10" lg="8">
                <v-row align="start" no-gutters>
                  <v-col cols="12" sm="5" class="pr-sm-1">
                    <template v-if="linkType === 'deeplinking'">
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <div v-bind="props">
                            <v-select
                              :items="availableDomainsOptions"
                              v-model="domainSelected"
                              @click="reloadDomainsSelected"
                              label="Domain"
                              :disabled="true"
                              variant="outlined"
                              density="compact"
                              required
                              class="domain-select"
                            >
                              <template v-slot:append-item>
                                <v-divider class="mb-2"></v-divider>
                                <v-list-item href="/#/domains" target="_blank">
                                  <template v-slot:prepend>
                                    <v-icon size="small">mdi-plus</v-icon>
                                  </template>

                                  <v-list-item-title class="text-grey-darken-2">
                                    Add new domain
                                  </v-list-item-title>
                                </v-list-item>
                              </template>
                            </v-select>
                          </div>
                        </template>
                        <span>
                          The domain is automatically set by the selected deep
                          linking configuration and cannot be changed.
                        </span>
                      </v-tooltip>
                    </template>
                    <template v-else>
                      <v-select
                        :items="availableDomainsOptions"
                        v-model="domainSelected"
                        @click="reloadDomainsSelected"
                        label="Domain"
                        :disabled="modelView == 'editLink'"
                        variant="outlined"
                        density="compact"
                        required
                        class="domain-select"
                      >
                        <template v-slot:append-item>
                          <v-divider class="mb-2"></v-divider>
                          <v-list-item href="/#/domains" target="_blank">
                            <template v-slot:prepend>
                              <v-icon size="small">mdi-plus</v-icon>
                            </template>

                            <v-list-item-title class="text-grey-darken-2">
                              Add custom domain
                            </v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-select>
                    </template>
                  </v-col>

                  <!-- hidden on a phone, where the two fields stack: the
                       preview below already shows the joined url -->
                  <v-col
                    cols="12"
                    sm="2"
                    class="text-center d-none d-sm-flex align-start justify-center pt-3"
                  >
                    <v-icon color="grey" class="url-separator-icon"
                      >mdi-slash-forward</v-icon
                    >
                  </v-col>

                  <v-col cols="12" sm="5" class="pl-sm-1">
                    <v-text-field
                      v-model="shortHandle"
                      label="Short Handle"
                      variant="outlined"
                      density="compact"
                      :disabled="modelView == 'editLink'"
                      validate-on="invalid-input"
                      :error-messages="serverErrors.shortHandle"
                      @update:model-value="clearServerError('shortHandle')"
                      :rules="shortHandleRules"
                      required
                      class="short-handle-input"
                    >
                      <!-- append-inner, not append: Vuetify 4 renders `append`
                           outside the outline, which left the button as a
                           white circle floating beside the box and squeezed
                           the validation message into the leftover width. -->
                      <template v-slot:append-inner>
                        <v-tooltip
                          location="bottom"
                          v-if="modelView == 'createLink'"
                        >
                          <template v-slot:activator="{ props }">
                            <v-btn
                              icon
                              variant="text"
                              size="x-small"
                              v-bind="props"
                              @click="shortHandle = randomShortHandle()"
                            >
                              <v-icon size="small">mdi-refresh</v-icon>
                            </v-btn>
                          </template>
                          <span>Generate random handle</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row
                  justify="center"
                  v-if="domainSelected && shortHandle"
                  class="mt-0"
                >
                  <v-col cols="12">
                    <v-alert type="info" density="compact" variant="tonal">
                      <div class="d-flex align-center link-preview">
                        <span class="text-body-small text-grey-darken-1 mr-2"
                          >Your link will look like:</span
                        >
                        <span class="text-primary"
                          >{{ domainSelected }}/{{ shortHandle }}</span
                        >
                      </div>
                    </v-alert>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </div>

          <!-- Apple App Store iOS -->
          <v-row v-if="linkType === 'simple'">
            <v-col cols="12">
              <v-text-field
                v-model="iosUrl"
                name="ios_url"
                label="Apple App Store (iOS)"
                placeholder="URL to your app on the Apple App Store for iOS devices, it should start with: https://apps.apple.com/..."
                rounded
                prepend-icon="mdi-apple"
                validate-on="invalid-input"
                :error-messages="serverErrors.iosUrl"
                @update:model-value="clearServerError('iosUrl')"
                :rules="iosUrlRules"
                required
                data-testid="link-dialog-ios-url"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Google Play Store Android -->
          <v-row v-if="linkType === 'simple'">
            <v-col cols="12">
              <v-text-field
                v-model="androidUrl"
                name="android_url"
                label="Google Play Store (Android)"
                placeholder="URL to your app on the Google Play Store for Android devices, it should start with: https://play.google.com/..."
                rounded
                prepend-icon="mdi-android"
                validate-on="invalid-input"
                :error-messages="serverErrors.androidUrl"
                @update:model-value="clearServerError('androidUrl')"
                :rules="androidUrlRules"
                required
                data-testid="link-dialog-android-url"
              ></v-text-field>
            </v-col>
          </v-row>

          <div
            v-if="linkType === 'deeplinking' && deepLinkingAppSelected"
            class="mb-5"
          >
            <v-row justify="center">
              <v-col cols="12" md="10" lg="8">
                <v-row no-gutters class="deep-linking-apps">
                  <v-col cols="12" sm="5">
                    <div>
                      <span>Android app:</span>
                      <v-icon>mdi-android</v-icon>
                      <span class="font-weight-bold ml-1"
                        ><a
                          style="text-decoration: none; color: inherit"
                          target="_blank"
                          :href="
                            'https://play.google.com/store/apps/details?id=' +
                            deepLinkingDomains.find(
                              (domain) =>
                                domain.domain === deepLinkingAppSelected,
                            ).androidConfigs[0].packageName
                          "
                          >{{
                            deepLinkingDomains.find(
                              (domain) =>
                                domain.domain === deepLinkingAppSelected,
                            ).androidConfigs[0].packageName
                          }}</a
                        ></span
                      >
                    </div>
                  </v-col>
                  <v-col cols="12" sm="5" offset-sm="2" class="pl-sm-1">
                    <div>
                      <span>iOS app:</span>
                      <v-icon>mdi-apple</v-icon>
                      <span class="font-weight-bold ml-1">
                        <a
                          style="text-decoration: none; color: inherit"
                          target="_blank"
                          :href="
                            'https://apps.apple.com/app/' +
                            deepLinkingDomains.find(
                              (domain) =>
                                domain.domain === deepLinkingAppSelected,
                            ).iosConfigs[0].appId
                          "
                          >{{
                            deepLinkingDomains.find(
                              (domain) =>
                                domain.domain === deepLinkingAppSelected,
                            ).iosConfigs[0].bundleId
                          }}</a
                        ></span
                      >
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </div>

          <!-- Huawei App Gallery -->
          <v-row align="center" justify="center" v-if="linkType === 'simple'">
            <v-col cols="12" sm="3">
              <p class="mb-0 mb-sm-7">Optional (Huawei devices only)</p>
            </v-col>
            <v-col cols="12" sm="9">
              <v-text-field
                v-model="huaweiAppGalleryUrl"
                name="huawei_app_gallery"
                label="Huawei App Gallery"
                placeholder="URL to your app on the Huawei App Gallery for Huawei devices that doesn't support Google Play Store, it should start with: https://appgallery.huawei.com/..."
                rounded
                validate-on="invalid-input"
                :error-messages="serverErrors.huaweiAppGalleryUrl"
                @update:model-value="clearServerError('huaweiAppGalleryUrl')"
                :rules="huaweiAppGalleryUrlRules"
              >
                <template v-slot:prepend>
                  <img
                    src="../assets/huawei_app_gallery.png"
                    class="huaweiAppGalleryIcon"
                  />
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-row align="center" justify="center">
            <v-col cols="12" md="2">
              <p class="text-no-wrap">
                <v-icon>mdi-monitor</v-icon> Desktop users
              </p>
            </v-col>
            <v-col cols="12" md="10">
              <v-radio-group v-model="desktopUserOption">
                <v-radio value="landing_page" @click="setLandingPageOption">
                  <template v-slot:label>
                    <div>
                      <span>Landing Page</span>
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            :hidden="!itHasLandingPage"
                            @click.stop="landingPageSettings = true"
                            class="ml-4"
                            color="primary"
                            variant="outlined"
                            icon
                            size="x-small"
                            v-bind="props"
                          >
                            <v-icon>mdi-cog</v-icon>
                          </v-btn>
                        </template>
                        <span>Advanced Landing page settings</span>
                      </v-tooltip>
                    </div>
                  </template>
                </v-radio>
                <p class="text-medium-emphasis">
                  Show an automatically generated landing page that matches your
                  app style with App Store and Play Store links and a QR code -
                  for desktop users only.
                </p>
                <!-- <v-tooltip
                  v-if="
                    modelView === 'editLink' &&
                    itHasLandingPage === true &&
                    desktopUserOption === 'landing_page'
                  "
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      :loading="regenerateLandingPageLoading"
                      @click="regenerateLandingPage"
                      v-on="on"
                      outlined
                      class="mb-5"
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Regenerate landing page
                    </v-btn>
                  </template>
                  <span
                    >If you have changed the app icon or description you can
                    regenerate the landing page, in this way the changes will be
                    reflected also on the landing page (note: data is sourced
                    from your app on Google Play Store)</span
                  >
                </v-tooltip> -->

                <v-divider class="mb-4"></v-divider>
                <v-radio
                  label="Redirect to website"
                  value="desktop_url"
                ></v-radio>
                <p class="text-medium-emphasis">
                  Redirect to an external website - for desktop users only.
                </p>
                <v-text-field
                  v-model="desktopUrl"
                  single-line
                  label="URL Address"
                  name="desktop_url"
                  placeholder="Enter the URL address where Desktop users will be redirected"
                  id="url_address"
                  @click="desktopUserOption = 'desktop_url'"
                  :hide-details="
                    desktopUserOption === 'desktop_url' ? 'auto' : true
                  "
                  validate-on="invalid-input"
                  :error-messages="serverErrors.desktopUrl"
                  @update:model-value="clearServerError('desktopUrl')"
                  :rules="desktopUrlRules"
                ></v-text-field>
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row>
            <v-expansion-panels v-model="notePanel" variant="accordion" flat>
              <v-expansion-panel value="note">
                <v-expansion-panel-title>
                  <div>
                    <v-icon class="mr-2 text-grey">mdi-cog-outline</v-icon>
                    <span>Optional note</span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-textarea
                    label="Notes for internal use"
                    class="mt-4"
                    v-model="note"
                    data-testid="link-dialog-note"
                  ></v-textarea>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-row>
        </v-container>
      </v-card-text>

      <!-- Create stays disabled until the link is complete: say why, with the
           first problem isLinkValid finds -->
      <p
        v-if="isLinkValid !== true"
        class="text-body-medium text-medium-emphasis text-center px-4 mb-0"
        data-testid="link-dialog-blocked-reason"
      >
        <v-icon size="small" class="mr-1">mdi-information-outline</v-icon
        >{{ isLinkValid }}
      </p>

      <v-card-actions @click="reportBlockedCreate">
        <!--<v-spacer></v-spacer>-->
        <!--<v-btn color="blue darken-1" text @click="close">Cancel</v-btn>-->
        <!-- `variant="flat"`: inside v-card-actions Vuetify 4 defaults a
             button to `text`, which left the primary action as bare green
             text on white and, disabled, as 26%-opacity green on white. -->
        <v-btn
          class="mb-2"
          color="success"
          size="large"
          variant="flat"
          rounded
          block
          @click="createOrUpdateLink"
          :loading="submitting"
          :disabled="isLinkValid !== true"
          data-testid="link-dialog-submit"
          >{{ modelView === "createLink" ? "Create" : "Update" }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  newLink as newLinkRequest,
  getLinkByLinkId,
  updateLink,
  refreshLandingPage,
  handleApiError,
} from "../api";
import { capture } from "../analytics";
import { qrCodeSvgDataUri } from "../qrCode";
import QrDownloadMenu from "./QrDownloadMenu.vue";
import eventBus from "../eventBus";
import { Confetti } from "vue-confetti";
import { mapState } from "vuex";
import isURL from "validator/es/lib/isURL";
import prependHttp from "prepend-http";

function componentToHex(c) {
  var hex = Number(c).toString(16);
  return hex.length == 1 ? "0" + hex : hex;
}

function rgbToHex(rgb) {
  const [r, g, b] = rgb
    .replace("rgb(", "")
    .replace(")", "")
    .split(",")
    .map((x) => x.trim());
  return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

// the api rejects anything that is not a real store url, and the form used to
// only check the https prefix, so any other link passed here and came back as
// a 400 on submit. these mirror ios/android verifyUrl in the backend
// src/util/appUrl.ts, keep them in sync with it
const IOS_STORE_URL_REGEX = new RegExp(
  "(?=itunes.apple.com)(?=.*id)|(?=apps.apple.com)(?=.*id)",
);
const ANDROID_STORE_URL_REGEX = new RegExp("(?=play.google.com)(?=.*id=)");

// mirrors every url field of the backend src/requests/link.ts: the api runs
// the value through toFullUrl (prepend-http) and then class-validator's
// @IsUrl() (validator's isURL), so a bare "example.com" is fine there while a
// url with a space is not. The form used to check only the http(s) prefix,
// which refused the first and let the second through to a 400
function isApiUrl(value) {
  return typeof value === "string" && isURL(prependHttp(value));
}

// mirrors TrustedImageURL in the backend src/requests/link.ts: the landing page
// icon is only accepted from the google/apple cdns
const TRUSTED_ICON_URL_PREFIXES = [
  "https://play-lh.googleusercontent.com/",
  "https://is1-ssl.mzstatic.com/image/",
];

// the api still rejects things the form cannot check on its own, a short handle
// already taken by someone else above all. map those messages back to the field
// they are about so the error shows under that input instead of in a detached
// browser alert. the landing page fields are deliberately not listed: their
// dialog is already closed by submit time, so an error there would be invisible
const SERVER_ERROR_FIELDS = [
  { field: "shortHandle", match: /short.?handle|url alias/i },
  { field: "iosUrl", match: /ios url/i },
  { field: "androidUrl", match: /android url/i },
  { field: "huaweiAppGalleryUrl", match: /huawei/i },
  { field: "desktopUrl", match: /desktop_url/i },
];

// the field each isLinkValid message is about, for the link_create_blocked
// event: read from the start of the message, the way SERVER_ERROR_FIELDS reads
// the api's. A new check in isLinkValid needs its field here
const LINK_PROBLEM_FIELDS = [
  { field: "shortHandle", match: /^short handle/i },
  { field: "domain", match: /^domain/i },
  { field: "iosUrl", match: /^ios/i },
  { field: "androidUrl", match: /^android/i },
  { field: "huaweiAppGalleryUrl", match: /^huawei/i },
  { field: "deepLinkingApp", match: /^deep linking app/i },
  { field: "deepLinkingPath", match: /^deep linking path/i },
  { field: "desktopUrl", match: /^desktop url/i },
];

const confetti = new Confetti();

export default {
  components: { QrDownloadMenu },
  data: () => ({
    // was read/written throughout this component (incl. the v-overlay at
    // :value="isLoading") without ever being declared here, so Vue 2 never
    // made it reactive and the overlay never actually showed or hid
    isLoading: false,
    // a create/update request is in flight; unlike isLoading it is only
    // cleared by that request finishing, never by re-opening the dialog
    submitting: false,
    deepLinkingAppSelected: null,
    deepLinkingPath: "/",
    shortHandle: "",
    domainSelected: "",
    // Preserve the user's previously selected domain when toggling deep linking
    previousDomainSelected: "",
    dialog: false,
    linkType: "simple", // default to simple, only editable when creating a new link
    regenerateLandingPageLoading: false,
    modelView: "createLink",

    // URL fields
    iosUrl: "",
    androidUrl: "",
    desktopUrl: "",
    huaweiAppGalleryUrl: "",

    // Desktop user option - default to landing page
    desktopUserOption: "landing_page",

    // Landing page settings
    landingPageSettings: false,
    landingPageSettingsUpdate: false,
    landingPageTitle: "",
    landingPageDescription: "",
    landingPageAppIconURL: "",
    landingPageColor: "#1976d2",
    itHasLandingPage: false,

    // Other properties
    editLinkId: null,
    fullUrl: "",
    fullUrlCopied: false,
    shareDialogTitle: "",
    originalHuaweiAppGalleryUrl: null,
    emailLink: "",
    twitterLink: "",

    // Validation rules
    shortHandleRules: [
      (v) => !!v || "Short handle is required",
      (v) =>
        (v && v.length >= 3) ||
        "Short handle must be at least 3 characters long",
      (v) =>
        new RegExp(/^[a-zA-Z0-9_-]*$/).test(v) ||
        "Allowed only alphanumeric characters and - _",
    ],
    iosUrlRules: [
      (v) => !!v || "App Store (iOS) url is required",
      (v) =>
        (v && v.startsWith("https://")) ||
        "App Store (iOS) url should start with https",
      (v) =>
        isApiUrl(v) ||
        "App Store (iOS) url is not a valid URL, check it for spaces",
      (v) =>
        IOS_STORE_URL_REGEX.test(v || "") ||
        "App Store (iOS) url must be an App Store link, e.g. https://apps.apple.com/us/app/name/id123456789",
    ],
    androidUrlRules: [
      (v) => !!v || "Play Store (Android) url is required",
      (v) =>
        (v && v.startsWith("https://")) ||
        "Play Store (Android) url should start with https",
      (v) =>
        isApiUrl(v) ||
        "Play Store (Android) url is not a valid URL, check it for spaces",
      (v) =>
        ANDROID_STORE_URL_REGEX.test(v || "") ||
        "Play Store (Android) url must be a Play Store link, e.g. https://play.google.com/store/apps/details?id=com.example.app",
    ],
    huaweiAppGalleryUrlRules: [
      (v) => {
        if (v === null || v === "") {
          return true;
        }
        if (v && !v.startsWith("https://")) {
          return "Huawei App Gallery url should start with https://";
        }
        if (!isApiUrl(v)) {
          return "Huawei App Gallery url is not a valid URL, check it for spaces";
        }
        return true;
      },
    ],
    desktopUrlRules: [
      (v) =>
        isApiUrl(v) ||
        "If 'Redirect to website' is selected, enter a valid URL for desktop users, e.g. example.com",
    ],
    landingPageTitleRules: [
      (v) => !!v || "App title is required",
      (v) =>
        (v && v.length >= 3) || "App title must be at least 3 characters long",
      (v) => (v && v.length <= 2048) || "App title is too long",
    ],
    landingPageDescriptionRules: [
      (v) => !!v || "App description is required",
      (v) =>
        (v && v.length >= 3) ||
        "App description must be at least 3 characters long",
      (v) => (v && v.length <= 2048) || "App description is too long",
    ],
    landingPageAppIconURLRules: [
      (v) => !!v || "App image url is required",
      (v) =>
        TRUSTED_ICON_URL_PREFIXES.some((prefix) =>
          (v || "").startsWith(prefix),
        ) ||
        "App image url must be the app icon from the Google Play Store or the App Store",
    ],
    note: "",
    // the note panel opens on its own when an edited link has a note
    notePanel: null,
    // landing page values the settings dialog was opened with, used to tell an
    // actual edit apart from just opening the dialog and hitting save
    landingPageOriginal: "",
    // api validation messages, keyed by the field they belong to. the keys are
    // declared upfront so vue 2 makes them reactive
    serverErrors: {
      shortHandle: "",
      iosUrl: "",
      androidUrl: "",
      huaweiAppGalleryUrl: "",
      desktopUrl: "",
    },
  }),
  computed: {
    ...mapState([
      "deepLinkingDomains",
      "availableDomainsStrippedDown",
      "availableDomains",
      "userPlan",
    ]),
    availableDomainsOptions() {
      // Build domain list depending on link type.
      // Only show domains with use_in_links (others are not ready yet).
      // For simple links: exclude domains marked as deepLinkingOnly
      try {
        const all = Array.isArray(this.availableDomains)
          ? this.availableDomains
          : [];
        const linkReady = all.filter((d) => d.use_in_links === true);
        const filtered =
          this.linkType === "simple"
            ? linkReady.filter((d) => !d.deepLinkingOnly)
            : linkReady;
        return filtered.map((d) => d.domain);
      } catch (e) {
        // Fallback to existing stripped down list in case of any issue
        return this.availableDomainsStrippedDown || [];
      }
    },
    isDeepLinkingPathValid() {
      if (this.linkType !== "deeplinking") return true;
      return (
        typeof this.deepLinkingPath === "string" &&
        this.deepLinkingPath.startsWith("/d/")
      );
    },
    isLinkValid() {
      // Check short handle
      if (!this.shortHandle) {
        return "Short handle is required";
      }

      // Check short handle minimum length
      if (this.shortHandle.length < 3) {
        return "Short handle must be at least 3 characters long";
      }

      // Check short handle format
      if (!new RegExp(/^[a-zA-Z0-9_-]*$/).test(this.shortHandle)) {
        return "Short handle can only contain alphanumeric characters and - _";
      }

      // a deep link takes its domain from the app (the domain select is
      // disabled, and switching to Deep Link clears it), so ask for the app
      // first: this message is the line shown above the disabled Create
      if (this.linkType === "deeplinking" && !this.deepLinkingAppSelected) {
        return "Deep linking app selection is required";
      }

      // Check domain selection
      if (!this.domainSelected) {
        return "Domain selection is required";
      }

      // Validate simple link URLs
      if (this.linkType === "simple") {
        if (!this.iosUrl) {
          return "iOS App Store URL is required";
        }
        if (!this.androidUrl) {
          return "Android Play Store URL is required";
        }
        if (!this.iosUrl.startsWith("https://")) {
          return "iOS URL must start with https://";
        }
        if (!this.androidUrl.startsWith("https://")) {
          return "Android URL must start with https://";
        }
        if (!isApiUrl(this.iosUrl)) {
          return "iOS URL is not a valid URL, check it for spaces";
        }
        if (!isApiUrl(this.androidUrl)) {
          return "Android URL is not a valid URL, check it for spaces";
        }
        if (!IOS_STORE_URL_REGEX.test(this.iosUrl)) {
          return "iOS URL must be an App Store link, e.g. https://apps.apple.com/us/app/name/id123456789";
        }
        if (!ANDROID_STORE_URL_REGEX.test(this.androidUrl)) {
          return "Android URL must be a Play Store link, e.g. https://play.google.com/store/apps/details?id=com.example.app";
        }
        if (
          this.huaweiAppGalleryUrl &&
          !this.huaweiAppGalleryUrl.startsWith("https://")
        ) {
          return "Huawei App Gallery URL must start with https://";
        }
        if (this.huaweiAppGalleryUrl && !isApiUrl(this.huaweiAppGalleryUrl)) {
          return "Huawei App Gallery URL is not a valid URL, check it for spaces";
        }
      }

      // Validate deep linking configuration
      if (this.linkType === "deeplinking") {
        if (!this.deepLinkingPath) {
          return "Deep linking path is required";
        }
        if (!(
          typeof this.deepLinkingPath === "string" &&
          this.deepLinkingPath.startsWith("/d/")
        )) {
          return "Deep linking path must start with /d/";
        }
      }

      // Validate desktop URL if selected
      if (this.desktopUserOption === "desktop_url") {
        if (!this.desktopUrl) {
          return "Desktop URL is required when 'Redirect to website' is selected";
        }
        if (!isApiUrl(this.desktopUrl)) {
          return "Desktop URL is not a valid URL, e.g. example.com";
        }
      }

      return true;
    },
    isLandingPageValid() {
      // the api validates the landing page as a whole, so a half filled form
      // can only ever be rejected: gate saving it the way the main form is
      const passes = (rules, value) => rules.every((r) => r(value) === true);
      return (
        passes(this.landingPageTitleRules, this.landingPageTitle) &&
        passes(this.landingPageDescriptionRules, this.landingPageDescription) &&
        passes(this.landingPageAppIconURLRules, this.landingPageAppIconURL)
      );
    },
  },
  watch: {
    // When user selects a deep linking configuration, automatically set the domain
    deepLinkingAppSelected(newVal) {
      if (this.linkType === "deeplinking" && newVal) {
        if (!this.previousDomainSelected) {
          this.previousDomainSelected = this.domainSelected;
        }
        this.domainSelected = newVal; // Set domain to the selected deep linking configuration's domain
      }
    },
    // When switching between link types, keep domain in sync
    linkType(newVal, oldVal) {
      if (newVal === "deeplinking") {
        // Going into deep linking mode: bind domain to the selected app if any if it's compatible for deep linking
        if (!this.previousDomainSelected) {
          this.previousDomainSelected = this.domainSelected;
        }
        if (this.deepLinkingAppSelected) {
          this.domainSelected = this.deepLinkingAppSelected;
        }
        // deepLinkingDomains holds one config object per app, not domain
        // strings: an includes() here never matched, so coming back to Deep
        // Link always emptied the domain, which is disabled on a deep link
        if (
          !this.deepLinkingDomains.some(
            (config) => config.domain === this.domainSelected,
          )
        ) {
          this.domainSelected = null;
        }
      } else if (oldVal === "deeplinking") {
        // Leaving deep linking mode: restore previous domain
        if (this.previousDomainSelected) {
          this.domainSelected = this.previousDomainSelected;
          this.previousDomainSelected = "";
        }
      }
    },
  },
  created() {
    // the app select lists these, whichever page shows the dialog: only the
    // Links page used to load them, so a Deep Link created from the overview
    // page had no app to pick
    this.$store.dispatch("getDeepLinkingDomains");
  },
  methods: {
    autoPrefixDeepLinkingPath() {
      if (this.linkType !== "deeplinking") return;
      let v = this.deepLinkingPath;
      if (typeof v !== "string") {
        this.deepLinkingPath = "/d/";
        return;
      }
      v = v.trim();
      if (v === "") {
        this.deepLinkingPath = "/d/";
        return;
      }
      if (!v.startsWith("/")) v = "/" + v;
      if (!v.startsWith("/d/")) {
        if (v === "/d") {
          v = "/d/";
        } else if (v.startsWith("/d") && !v.startsWith("/d/")) {
          v = v.replace("/d", "/d/");
        } else {
          v = v.replace(/^\/+/, "");
          v = "/d/" + v;
        }
      }
      this.deepLinkingPath = v;
    },
    randomShortHandle(length = 6) {
      const chars =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let result = "";
      for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    },
    handleShareResponse(res) {
      if (res && "status" in res && res.status === 200) {
        this.fullUrl = res.data.full_url;
        const prevView = this.modelView;
        this.modelView = "shareLink";
        // Reset loading state when switching to share view
        this.isLoading = false;

        this.qrCodeImgSrc = qrCodeSvgDataUri(this.fullUrl);

        // Set email and Twitter sharing links
        this.emailLink = `mailto:?subject=Check out this app&body=${encodeURIComponent(
          this.fullUrl,
        )}`;
        this.twitterLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          "Check out this app: " + this.fullUrl,
        )}`;

        // run only on desktop / widescreen and on create
        if (window.innerWidth >= 1300 && prevView === "createLink") {
          // You can add any additional logic here if needed
        }
      }
    },
    openShareDialog(fullUrl, linkId) {
      this.dialog = true;
      this.modelView = "shareLink";
      this.fullUrl = fullUrl || "";
      this.fullUrlCopied = false;
      this.editLinkId = linkId || null;
      // Reset loading state when switching to share view
      this.isLoading = false;
      this.qrCodeImgSrc = this.fullUrl ? qrCodeSvgDataUri(this.fullUrl) : "";
      // Set email and Twitter sharing links
      this.emailLink = this.fullUrl
        ? `mailto:?subject=Check out this app&body=${encodeURIComponent(
            this.fullUrl,
          )}`
        : "";
      this.twitterLink = this.fullUrl
        ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            "Check out this app: " + this.fullUrl,
          )}`
        : "";
    },
    open() {
      this.dialog = true;
      // Reset loading state when opening the dialog
      this.isLoading = false;
    },
    close() {
      this.dialog = false;
    },
    landingPageSnapshot() {
      return JSON.stringify([
        this.landingPageTitle,
        this.landingPageDescription,
        this.landingPageAppIconURL,
        this.landingPageColor,
      ]);
    },
    clearServerError(field) {
      this.serverErrors[field] = "";
    },
    resetServerErrors() {
      for (const field of Object.keys(this.serverErrors)) {
        this.serverErrors[field] = "";
      }
    },
    /**
     * put an api validation message under the field it is about. returns false
     * when it belongs to no particular field, so the caller can still alert
     */
    showServerError(message) {
      const hit = SERVER_ERROR_FIELDS.find((f) => f.match.test(message || ""));
      if (!hit) return false;
      this.serverErrors[hit.field] = message;
      return true;
    },
    /**
     * a 402 is a plan limit (link quota, landing page customization), which
     * only an upgrade gets past. A bare alert left people searching the form
     * for what they had typed wrong
     */
    offerPlanPage(message) {
      if (window.confirm(`${message}\n\nOpen the Plan page?`)) {
        this.close();
        this.$router.push("/plan");
      }
    },
    /**
     * Create stays disabled while isLinkValid finds a problem, and Vuetify
     * gives a disabled v-btn pointer-events: none, so a click on it lands on
     * the v-card-actions row this is bound to. That click is the attempt
     * link_create_blocked counts; a click on the enabled button bubbles here
     * too, and is not one
     */
    reportBlockedCreate() {
      if (this.modelView !== "createLink" || this.isLinkValid === true) return;
      const hit = LINK_PROBLEM_FIELDS.find((f) =>
        f.match.test(this.isLinkValid),
      );
      capture("link_create_blocked", {
        field: hit?.field ?? null,
        message: this.isLinkValid,
      });
    },
    saveLandingPageSettings() {
      this.landingPageSettings = false;
      // only flag an update when the values actually differ from the ones the
      // dialog opened with: saving an untouched (or empty) form used to send
      // landing_page anyway and get a 400 back
      this.landingPageSettingsUpdate =
        this.landingPageSnapshot() !== this.landingPageOriginal;
    },
    async init(editLinkId) {
      this.editLinkId = editLinkId;
      // the domain to give back when leaving Deep Link belongs to one opening
      // of the dialog: kept from the last one (a deep link edited), the
      // linkType watcher put that link's domain on the next new link
      this.previousDomainSelected = "";

      // Clear form when opening a new link to prevent showing stale data
      if (!editLinkId) {
        // New link: set defaults
        this.modelView = "createLink";
        this.domainSelected =
          this.userPlan?.user?.default_domain_namespace || "link-to.app";
        this.shortHandle = this.randomShortHandle();
        this.deepLinkingAppSelected = null;
        this.deepLinkingPath = "/d/";
        this.iosUrl = "";
        this.androidUrl = "";
        this.desktopUrl = "";
        this.huaweiAppGalleryUrl = "";
        this.desktopUserOption = "landing_page"; // Default to landing page
        this.note = "";
        this.notePanel = null;
        this.linkType = "simple";

        // Reset landing page settings
        this.landingPageTitle = "";
        this.landingPageDescription = "";
        this.landingPageAppIconURL = "";
        this.landingPageColor = "#1976d2";
        this.itHasLandingPage = false;
        this.landingPageSettings = false;
        this.landingPageSettingsUpdate = false;
        this.landingPageOriginal = this.landingPageSnapshot();
      } else {
        this.isLoading = true;

        // Editing existing link: fetch and set values from link
        this.modelView = "editLink";
        // this branch never reset the flag, so a landing page edit on one link
        // kept sending landing_page for every link opened afterwards. reset
        // before the fetch so the early return and the catch below are covered
        this.landingPageSettings = false;
        this.landingPageSettingsUpdate = false;
        this.landingPageOriginal = this.landingPageSnapshot();
        this.notePanel = null;
        try {
          const link = await getLinkByLinkId(editLinkId);

          if (!link) {
            console.error("Error: link is undefined in response", link);
            window.alert(
              "Error: Link data could not be loaded. Please try again or refresh the page.",
            );
            this.isLoading = false;
            return;
          }

          if (link.type === "deeplinking") {
            this.linkType = "deeplinking";
            // Set the selected app to the domain string, matching <v-select :item-value="domain">
            this.deepLinkingAppSelected =
              link.deeplinking_domain?.domain || null;
            this.deepLinkingPath = link.deeplinking_path || "/";
            this.domainSelected =
              link.domain_namespace ||
              this.userPlan?.user?.default_domain_namespace ||
              "link-to.app";
            this.shortHandle = link.short_handle || "";
            this.iosUrl = link.ios?.url || "";
            this.androidUrl = link.android?.url || "";
            this.desktopUrl = link.desktop_url || "";
            this.huaweiAppGalleryUrl = link.huawei_app_gallery?.url || "";
            this.originalHuaweiAppGalleryUrl =
              link.huawei_app_gallery?.url || "";
            this.desktopUserOption = link.desktop_url
              ? "desktop_url"
              : "landing_page";
            this.note = link.note || "";
          } else {
            this.linkType = "simple";
            this.deepLinkingAppSelected = link.deeplinking_domain || null;
            this.deepLinkingPath = link.deeplinking_path || "/";
            this.domainSelected =
              link.domain_namespace ||
              this.userPlan?.user?.default_domain_namespace ||
              "link-to.app";
            this.shortHandle = link.short_handle || "";
            this.iosUrl = link.ios?.url || "";
            this.androidUrl = link.android?.url || "";
            this.desktopUrl = link.desktop_url || "";
            this.huaweiAppGalleryUrl = link.huawei_app_gallery?.url || "";
            this.originalHuaweiAppGalleryUrl =
              link.huawei_app_gallery?.url || "";
            this.desktopUserOption = link.desktop_url
              ? "desktop_url"
              : "landing_page";
            this.note = link.note || "";
          }
          this.originalHuaweiAppGalleryUrl = link.huawei_app_gallery?.url || "";
          this.desktopUserOption = link.desktop_url
            ? "desktop_url"
            : "landing_page";
          this.note = link.note || "";
          this.notePanel = this.note ? "note" : null;

          // Set landing page settings if available
          if (link.landing_page) {
            this.landingPageTitle = link.landing_page.title || "";
            this.landingPageDescription =
              link.landing_page.short_description || "";
            this.landingPageAppIconURL = link.landing_page.icon_url || "";
            this.landingPageColor = link.landing_page.main_color || "#1976d2";
            this.itHasLandingPage = true;
          } else {
            this.itHasLandingPage = false;
          }
          // re-snapshot now the loaded values are in place
          this.landingPageOriginal = this.landingPageSnapshot();
        } catch (error) {
          console.error("Error fetching link:", error);
          window.alert("Error loading link data");
        }
      }
      this.isLoading = false;
    },
    async createOrUpdateLink() {
      // guard against double-submit (e.g. a double click / Enter): a second
      // call before the first request resolves would fire a duplicate
      // create/update request
      if (this.isLoading || this.submitting) return;

      const cleanLink = {
        short_handle: this.shortHandle,
      };

      // Only include type when creating a new link
      if (this.modelView === "createLink") {
        cleanLink.type =
          this.linkType === "deeplinking" ? "deeplinking" : "simple";
      }

      // Add URLs for simple links
      if (this.linkType === "simple") {
        cleanLink.ios = { url: this.iosUrl };
        cleanLink.android = { url: this.androidUrl };
        if (this.huaweiAppGalleryUrl) {
          cleanLink.huawei_app_gallery = {
            url: this.huaweiAppGalleryUrl,
          };
        }
      }

      // Add deep linking configuration
      if (this.linkType === "deeplinking") {
        // Add the new required fields for deep link support
        cleanLink.deeplinking_domain = this.deepLinkingAppSelected;
        cleanLink.deeplinking_path = this.deepLinkingPath;
      }

      // Add desktop URL if selected
      if (this.desktopUserOption === "desktop_url") {
        cleanLink.desktop_url = this.desktopUrl;
      }

      // Add domain namespace
      if (this.domainSelected) {
        cleanLink.domain_namespace = this.domainSelected;
      }

      // Add note if provided
      if (this.note) {
        cleanLink.note = this.note;
      }

      this.isLoading = true;
      this.submitting = true;
      let res;
      let alertTriggered = false;
      try {
        this.resetServerErrors();
        if (this.modelView === "createLink") {
          this.shareDialogTitle = "Your Link is now live";
          capture("link_create_submitted", { type: cleanLink.type });
          res = await newLinkRequest(cleanLink).catch((err) => {
            alertTriggered = true;
            const message = handleApiError(err);
            if (err?.response?.status === 402) {
              this.offerPlanPage(message);
            } else if (!this.showServerError(message)) {
              window.alert(message);
            }
            capture("link_create_failed", {
              status: err?.response?.status,
              message,
            });
          });
        } else if (this.modelView === "editLink") {
          this.shareDialogTitle = "Your link is now updated and live";
          cleanLink.note = this.note;
          cleanLink.desktop_url =
            this.desktopUserOption === "landing_page" ? null : this.desktopUrl;

          // landing page settings changed
          if (this.landingPageSettingsUpdate === true) {
            cleanLink.landing_page = {
              title: this.landingPageTitle,
              short_description: this.landingPageDescription,
              icon_url: this.landingPageAppIconURL,
              main_color: this.landingPageColor,
            };
          }

          // huawei app gallery removed
          if (!!this.originalHuaweiAppGalleryUrl && !this.huaweiAppGalleryUrl) {
            cleanLink.huawei_app_gallery = null;
          }

          const { short_handle, domain_namespace, ...editLinkReq } = cleanLink;
          res = await updateLink(this.editLinkId, editLinkReq).catch((err) => {
            alertTriggered = true;
            const message = handleApiError(err);
            if (err?.response?.status === 402) {
              this.offerPlanPage(message);
            } else if (!this.showServerError(message)) {
              window.alert(message);
            }
          });
        }

        if (res && "status" in res && res.status === 200) {
          this.fullUrl = res.data.full_url;
          this.isLoading = false; // Ensure loading is false before showing share dialog

          this.qrCodeImgSrc = qrCodeSvgDataUri(this.fullUrl);

          const prevView = this.modelView;
          this.modelView = "shareLink";

          // Set email and Twitter sharing links
          this.emailLink = `mailto:?subject=Check out this app&body=${encodeURIComponent(
            this.fullUrl,
          )}`;
          this.twitterLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            "Check out this app: " + this.fullUrl,
          )}`;

          // run only on desktop / widescreen and on create
          if (window.innerWidth >= 1300 && prevView === "createLink") {
            confetti.start({ particlesPerFrame: 0.5 });
            setTimeout(() => {
              confetti.stop();
            }, 2400);
          }
          eventBus.emit("linkListFetch", 1);
        } else {
          if (alertTriggered === false) {
            window.alert(res.data.message);
          }
        }
        console.log(res);
      } finally {
        // covers the create/update failure path too: without this the overlay
        // stayed up after an error and the dialog needed a reload to use again
        this.isLoading = false;
        this.submitting = false;
      }
    },
    setLandingPageOption() {
      this.desktopUserOption = "landing_page";
    },
    reloadDomainsSelected() {
      // Reload available domains if needed
      this.$store.dispatch("getAvailableDomains");
    },
    async regenerateLandingPage() {
      if (!this.editLinkId) return;

      this.regenerateLandingPageLoading = true;
      try {
        await refreshLandingPage(this.editLinkId);
        window.alert("Landing page regenerated successfully");
      } catch (error) {
        console.error("Error regenerating landing page:", error);
        window.alert("Error regenerating landing page");
      } finally {
        this.regenerateLandingPageLoading = false;
      }
    },
    copyFullUrl() {
      const textField = this.$refs.fullUrlInput;
      if (textField) {
        // Try Vuetify's internal input ref first
        const inputEl =
          textField.$refs && textField.$refs.input
            ? textField.$refs.input
            : textField.$el.querySelector("input");
        if (inputEl && typeof inputEl.select === "function") {
          inputEl.select();
          document.execCommand("copy");
          this.fullUrlCopied = true;
          setTimeout(() => {
            this.fullUrlCopied = false;
          }, 2000);
        }
      }
    },
  },
};
</script>
<style>
/* Every other modal in the app is an `.ln-dialog` div styled from the
   index.html design tokens; this component is the one still built out of
   v-card, so it took Vuetify 4's own 4px-radius surface and read as a
   different kind of window. Match the tokens the other dialogs use.
   Vuetify 4 ships its CSS in @layer, so these unlayered rules win without
   !important. */
.ln-dialog-card.v-card {
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
}

/* full screen (phones): the card is the whole screen, not a window on it.
   With the dialog's own 24px margins gone, also drop the container's extra
   gutter and the card's 24px sides, which together left the fields 230px
   wide on a 360px screen. Scoped to the full screen dialog, so the floating
   landing page settings card keeps its padding. */
.v-dialog--fullscreen .ln-dialog-card.v-card {
  border: 0;
  border-radius: 0;
}

.v-dialog--fullscreen .ln-dialog-card .v-container {
  padding-left: 0;
  padding-right: 0;
}

.v-dialog--fullscreen .ln-dialog-card > .v-card-text {
  padding-left: 16px;
  padding-right: 16px;
}

/* only rendered on a phone; sits on the first line of the title, which
   stops short of it */
.link-dialog-close {
  position: absolute;
  top: 8px;
  right: 8px;
}

.link-dialog-close ~ .v-card-title .link-dialog-title {
  max-width: calc(100% - 44px);
}

/* a class rather than an inline style, so a phone can let it shrink (the
   override, in the max-width: 599px block below, has to come after this) */
.link-type-picker {
  min-width: 320px;
}

/* v4's .v-card-title is nowrap + ellipsis and half the padding v2 used; the
   title here is a two-column row (heading + link-type chips), which needs to
   wrap on a narrow screen rather than be clipped */
.ln-dialog-card .v-card-title {
  padding: 16px 16px 8px;
  white-space: normal;
}

/* v4 draws an outlined card's border with currentColor, which here is the
   app's near-black ink, so the Deep Linking Details panel was framed in a
   heavy black rule instead of the light one every other card uses. */
.ln-dialog-card .v-card--variant-outlined {
  border-color: var(--ln-line);
  border-radius: var(--ln-radius-sm);
}

/* the select is the same compact height and font as the Path field beside it
   (it used to be 50px / 1.1rem, so the two sat misaligned and oversized) */
.deep-linking-select .v-select__selection {
  font-size: 1rem;
}

/* "Android app: <id>" / "iOS app: <id>" line: small body text, icon centred on
   the text and the pair laid out on the same grid as the domain row above */
.deep-linking-apps > .v-col > div {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

/* v-card-title's 1.5rem leaked into the outlined button's label */
.ln-dialog-card .landing-regenerate {
  font-size: 0.875rem;
}

/* v4's .v-field__input wraps its children, so a long app name broke onto a
   second line and pushed the icon off the field's centre -- keep the closed
   selection on one line and ellipsise it instead */
.deep-linking-select .v-select__selection,
.deep-linking-select__selection {
  min-width: 0;
  overflow: hidden;
}

.deep-linking-select__selection span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* on a phone the field is barely wider than its two icons, which ellipsises
   the app name down to a few letters -- drop the decorative mdi-apps icon
   sitting outside the field and give that width to the name */
@media (max-width: 599px) {
  .deep-linking-select > .v-input__prepend,
  .deep-linking-path > .v-input__prepend {
    display: none;
  }

  /* the 28px app icon beside a 1rem name ellipsised "Example App" on a
     phone; step it down so the name fits */
  .deep-linking-select .v-select__selection {
    font-size: 0.95rem;
  }

  /* the two chips need 300px, which a 320px screen does not have once the
     title's padding is taken off: let the picker shrink and the chips wrap */
  .link-type-picker {
    min-width: 0;
  }

  /* "Your link will look like:" wrapped one word per line beside the url,
     and a long domain ran out of the box */
  .link-preview {
    flex-wrap: wrap;
  }

  .link-preview .text-primary {
    overflow-wrap: anywhere;
  }

  /* same for a long package name / bundle id (production has 36 chars),
     which ran past the edge and made the whole dialog scroll sideways */
  .deep-linking-apps > .v-col > div {
    flex-wrap: wrap;
  }

  .deep-linking-apps a {
    overflow-wrap: anywhere;
  }
}

/* the app icon is rendered both in the menu list and in the closed
   selection, so it is a class rather than a repeated inline style */
.deep-linking-select__icon {
  height: 24px;
  width: 24px;
  object-fit: contain;
  border-radius: 6px;
}

/* Short Handle Section Styling */
.short-handle-section {
  margin: 16px 0;
}

.domain-select,
.short-handle-input {
  min-height: 72px; /* Reserve space for error messages */
  --v-input-control-height: 48px;
}

/* Vuetify 4 hides overflow on the details area and gives messages a 12px
   line-height, so a two-line error lost its descenders under the preview
   banner. Let the lines breathe and keep a gap below. */
.short-handle-input .v-messages__message {
  line-height: 14px;
}

.short-handle-input .v-input__details {
  padding-bottom: 8px;
}

.domain-select .v-field__input,
.domain-select .v-select__selection,
.short-handle-input input {
  font-size: 1.1rem;
  font-weight: 500;
}

.url-preview-alert {
  border-left: 4px solid #1976d2;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .url-separator-icon {
    margin: 8px 0;
  }
}

#confetti-canvas {
  z-index: 1000;
}

.huaweiAppGalleryIcon {
  width: 31px;
  margin-top: -4px;
}

/* a class rather than an inline style, so a phone can step the font down
   (the override has to come after this rule) */
.share-url {
  max-width: 380px;
  font-size: 1.4em;
  --v-input-control-height: 50px;
}

@media (max-width: 599px) {
  .share-url {
    font-size: 1rem;
  }
}

.qr-split {
  display: flex;
  gap: 1px;
}

.qr-split__main {
  flex: 1 1 auto;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.qr-split__more.v-btn {
  min-width: 40px;
  padding: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.qr-code-image {
  width: 100%;
  max-width: 200px;
  height: auto;
  display: block;
  margin: 0 auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
